import {
  drawStickerPlacements,
  getStickerOutputPixelRect,
  TCanvasSmoothingQuality,
  TNormalizedStickerPlacement,
} from "../../../../utils/canvasStickerExport";

export const LAB_CANVAS_SIZE = { width: 960, height: 640 };
export const LAB_MATCHED_CSS_SIZE = { width: 360, height: 240 };
export const LAB_PLACEMENT: TNormalizedStickerPlacement = {
  x: 0.1734,
  y: 0.2127,
  width: 0.4133,
  height: 0.3617,
};

export type TRenderLabMetrics = {
  meanAbsoluteRgbaDifference: number;
  maximumRgbaDifference: number;
  rootMeanSquareRgbaDifference: number;
  ssim: number;
  edgeEnergyRatio: number;
  edgeSpreadPixels: number;
  flatRegionVariance: number;
};

export type TRenderLabVariant = {
  id: string;
  label: string;
  description: string;
  outputUrl: string;
  matchedUrl: string;
  smoothingQuality:
    | TCanvasSmoothingQuality
    | "disabled"
    | "direct-dom-equivalent";
  alignedToPixels: boolean;
  sourceKind:
    | "96px-png"
    | "780px-png"
    | "1560px-png"
    | "svg"
    | "image-bitmap"
    | "780px-profile-stripped"
    | "780px-opaque-png";
  resamplingPasses: number;
  outputRectangle: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  metrics: TRenderLabMetrics;
  encodeDecode: {
    meanAbsoluteRgbaDifference: number;
    maximumRgbaDifference: number;
  };
};

export type TRenderLabResult = {
  generatedAt: string;
  userAgent: string;
  devicePixelRatio: number;
  canvasSize: typeof LAB_CANVAS_SIZE;
  matchedSize: {
    cssWidth: number;
    cssHeight: number;
    pixelWidth: number;
    pixelHeight: number;
  };
  placement: typeof LAB_PLACEMENT;
  referenceStickerUrl: string;
  defaultContextSettings: {
    imageSmoothingEnabled: boolean;
    imageSmoothingQuality: ImageSmoothingQuality;
  };
  variants: TRenderLabVariant[];
  experimentSummary: {
    smoothingMeanDifferenceRange: number;
    highVsDefaultMeanDifference: number;
    alignedVsFractionalMeanDifference: number;
    highResolutionImprovementPercent: number;
    svgImprovementPercent: number;
    imageBitmapImprovementPercent: number | null;
    doubleResamplingPenaltyPercent: number;
    alphaVsOpaqueErrorDifference: number;
    encodeDecodeMaximumDifference: number;
  };
};

const fixtureUrl = (filename: string) => `/render-quality-fixtures/${filename}`;

const loadImage = (url: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error(`Render-quality fixture failed to load: ${url}`));
    image.src = url;
  });

const canvasToBlob = (canvas: HTMLCanvasElement) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Canvas PNG encoding returned no data."));
    }, "image/png");
  });

const canvasToImageData = (canvas: HTMLCanvasElement) => {
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("Canvas pixels could not be read.");
  return context.getImageData(0, 0, canvas.width, canvas.height);
};

const decodeBlob = async (blob: Blob) => {
  const url = URL.createObjectURL(blob);
  try {
    const image = await loadImage(url);
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) throw new Error("Decoded PNG pixels could not be read.");
    context.drawImage(image, 0, 0);
    return { image, canvas, imageData: canvasToImageData(canvas) };
  } finally {
    URL.revokeObjectURL(url);
  }
};

const rasterizeImage = async (
  image: CanvasImageSource,
  width: number,
  height: number,
) => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Fixture rasterization is unavailable.");
  context.drawImage(image, 0, 0, width, height);
  const blob = await canvasToBlob(canvas);
  return { ...(await decodeBlob(blob)), blob };
};

const stripPngColorProfile = async (blob: Blob) => {
  const source = new Uint8Array(await blob.arrayBuffer());
  const chunks: Uint8Array[] = [source.slice(0, 8)];
  const decoder = new TextDecoder("ascii");
  const profileChunkTypes = new Set(["cHRM", "gAMA", "iCCP", "sRGB"]);
  let offset = 8;

  while (offset + 12 <= source.length) {
    const dataLength =
      source[offset] * 0x1000000 +
      source[offset + 1] * 0x10000 +
      source[offset + 2] * 0x100 +
      source[offset + 3];
    const chunkEnd = offset + 12 + dataLength;
    if (chunkEnd > source.length) {
      throw new Error("Synthetic PNG contains an invalid chunk length.");
    }
    const chunkType = decoder.decode(source.slice(offset + 4, offset + 8));
    if (!profileChunkTypes.has(chunkType)) {
      chunks.push(source.slice(offset, chunkEnd));
    }
    offset = chunkEnd;
    if (chunkType === "IEND") break;
  }

  return new Blob(chunks, { type: "image/png" });
};

const createCanvas = (width: number, height: number) => {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
};

const getMatchedPixelSize = () => ({
  width: Math.max(
    1,
    Math.round(LAB_MATCHED_CSS_SIZE.width * window.devicePixelRatio),
  ),
  height: Math.max(
    1,
    Math.round(LAB_MATCHED_CSS_SIZE.height * window.devicePixelRatio),
  ),
});

const getCrop = (
  source: ImageData,
  bounds: { x: number; y: number; width: number; height: number },
) => {
  const x = Math.max(0, Math.floor(bounds.x));
  const y = Math.max(0, Math.floor(bounds.y));
  const right = Math.min(source.width, Math.ceil(bounds.x + bounds.width));
  const bottom = Math.min(source.height, Math.ceil(bounds.y + bounds.height));
  const width = Math.max(1, right - x);
  const height = Math.max(1, bottom - y);
  const output = new ImageData(width, height);

  for (let row = 0; row < height; row += 1) {
    const sourceStart = ((y + row) * source.width + x) * 4;
    const targetStart = row * width * 4;
    output.data.set(
      source.data.subarray(sourceStart, sourceStart + width * 4),
      targetStart,
    );
  }
  return output;
};

const luminance = (data: Uint8ClampedArray, index: number) =>
  data[index] * 0.2126 + data[index + 1] * 0.7152 + data[index + 2] * 0.0722;

const comparePixels = (
  reference: ImageData,
  candidate: ImageData,
): Pick<
  TRenderLabMetrics,
  | "meanAbsoluteRgbaDifference"
  | "maximumRgbaDifference"
  | "rootMeanSquareRgbaDifference"
  | "ssim"
> => {
  if (
    reference.width !== candidate.width ||
    reference.height !== candidate.height
  ) {
    throw new Error("Pixel comparisons require equal dimensions.");
  }

  let absoluteDifference = 0;
  let squaredDifference = 0;
  let maximumDifference = 0;
  let referenceMean = 0;
  let candidateMean = 0;
  const pixelCount = reference.width * reference.height;

  for (let index = 0; index < reference.data.length; index += 4) {
    referenceMean += luminance(reference.data, index);
    candidateMean += luminance(candidate.data, index);
    for (let channel = 0; channel < 4; channel += 1) {
      const difference = Math.abs(
        reference.data[index + channel] - candidate.data[index + channel],
      );
      absoluteDifference += difference;
      squaredDifference += difference * difference;
      maximumDifference = Math.max(maximumDifference, difference);
    }
  }

  referenceMean /= pixelCount;
  candidateMean /= pixelCount;
  let referenceVariance = 0;
  let candidateVariance = 0;
  let covariance = 0;
  for (let index = 0; index < reference.data.length; index += 4) {
    const referenceDelta = luminance(reference.data, index) - referenceMean;
    const candidateDelta = luminance(candidate.data, index) - candidateMean;
    referenceVariance += referenceDelta * referenceDelta;
    candidateVariance += candidateDelta * candidateDelta;
    covariance += referenceDelta * candidateDelta;
  }
  const varianceDivisor = Math.max(1, pixelCount - 1);
  referenceVariance /= varianceDivisor;
  candidateVariance /= varianceDivisor;
  covariance /= varianceDivisor;
  const c1 = (0.01 * 255) ** 2;
  const c2 = (0.03 * 255) ** 2;
  const ssim =
    ((2 * referenceMean * candidateMean + c1) * (2 * covariance + c2)) /
    ((referenceMean ** 2 + candidateMean ** 2 + c1) *
      (referenceVariance + candidateVariance + c2));

  return {
    meanAbsoluteRgbaDifference: absoluteDifference / reference.data.length,
    maximumRgbaDifference: maximumDifference,
    rootMeanSquareRgbaDifference: Math.sqrt(
      squaredDifference / reference.data.length,
    ),
    ssim,
  };
};

const edgeEnergy = (image: ImageData) => {
  let total = 0;
  let samples = 0;
  for (let y = 0; y < image.height - 1; y += 1) {
    for (let x = 0; x < image.width - 1; x += 1) {
      const index = (y * image.width + x) * 4;
      const right = index + 4;
      const down = index + image.width * 4;
      total +=
        Math.abs(luminance(image.data, index) - luminance(image.data, right)) +
        Math.abs(luminance(image.data, index) - luminance(image.data, down));
      samples += 2;
    }
  }
  return total / Math.max(1, samples);
};

const edgeSpreadPixels = (
  image: ImageData,
  matchedRectangle: TRenderLabVariant["outputRectangle"],
) => {
  const y = Math.round(matchedRectangle.y + matchedRectangle.height * 0.27);
  const centerX = Math.round(matchedRectangle.x + matchedRectangle.width * 0.5);
  let spread = 0;
  for (
    let x = Math.max(0, centerX - 8);
    x <= Math.min(image.width - 1, centerX + 8);
    x += 1
  ) {
    const value = luminance(image.data, (y * image.width + x) * 4);
    if (value > 25.5 && value < 229.5) spread += 1;
  }
  return spread;
};

const regionVariance = (
  image: ImageData,
  bounds: { x: number; y: number; width: number; height: number },
) => {
  const crop = getCrop(image, bounds);
  let mean = 0;
  const values: number[] = [];
  for (let index = 0; index < crop.data.length; index += 4) {
    const value = luminance(crop.data, index);
    values.push(value);
    mean += value;
  }
  mean /= Math.max(1, values.length);
  return (
    values.reduce((total, value) => total + (value - mean) ** 2, 0) /
    Math.max(1, values.length)
  );
};

const renderMatched = (
  source: CanvasImageSource,
  smoothingQuality: TCanvasSmoothingQuality = "default",
) => {
  const matchedPixelSize = getMatchedPixelSize();
  const canvas = createCanvas(matchedPixelSize.width, matchedPixelSize.height);
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("Matched-size Canvas is unavailable.");
  if (smoothingQuality !== "default") {
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = smoothingQuality;
  }
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
};

const renderDirectReference = ({
  background,
  sticker,
  placement = LAB_PLACEMENT,
}: {
  background: CanvasImageSource;
  sticker: CanvasImageSource;
  placement?: TNormalizedStickerPlacement;
}) => {
  const matchedPixelSize = getMatchedPixelSize();
  const canvas = createCanvas(matchedPixelSize.width, matchedPixelSize.height);
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("Direct reference Canvas is unavailable.");
  context.drawImage(background, 0, 0, canvas.width, canvas.height);
  drawStickerPlacements({
    context,
    stickerImage: sticker,
    placements: [placement],
    canvasWidth: canvas.width,
    canvasHeight: canvas.height,
  });
  return canvas;
};

type TVariantInput = {
  id: string;
  label: string;
  description: string;
  sticker: CanvasImageSource;
  sourceKind: TRenderLabVariant["sourceKind"];
  smoothingQuality?: TCanvasSmoothingQuality;
  smoothingEnabled?: boolean;
  alignedToPixels?: boolean;
  placement?: TNormalizedStickerPlacement;
};

const createVariant = async ({
  input,
  background,
  directReferencePixels,
}: {
  input: TVariantInput;
  background: CanvasImageSource;
  directReferencePixels: ImageData;
}): Promise<TRenderLabVariant> => {
  const canvas = createCanvas(LAB_CANVAS_SIZE.width, LAB_CANVAS_SIZE.height);
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new Error("Export Canvas is unavailable.");
  context.drawImage(background, 0, 0, canvas.width, canvas.height);
  if (input.smoothingEnabled === false) {
    context.imageSmoothingEnabled = false;
  }
  const placement = input.placement ?? LAB_PLACEMENT;
  const [outputRectangle] = drawStickerPlacements({
    context,
    stickerImage: input.sticker,
    placements: [placement],
    canvasWidth: canvas.width,
    canvasHeight: canvas.height,
    smoothingQuality: input.smoothingQuality,
    alignToPixels: input.alignedToPixels,
  });
  const preEncodePixels = canvasToImageData(canvas);
  const blob = await canvasToBlob(canvas);
  const decoded = await decodeBlob(blob);
  const encodeDecode = comparePixels(preEncodePixels, decoded.imageData);
  const matchedCanvas = renderMatched(decoded.image);
  const matchedPixels = canvasToImageData(matchedCanvas);
  const matchedRectangle = getStickerOutputPixelRect({
    placement,
    canvasWidth: matchedPixels.width,
    canvasHeight: matchedPixels.height,
    alignToPixels: input.alignedToPixels,
  });
  const referenceCrop = getCrop(directReferencePixels, matchedRectangle);
  const candidateCrop = getCrop(matchedPixels, matchedRectangle);
  const comparison = comparePixels(referenceCrop, candidateCrop);
  const flatBounds = {
    x: matchedRectangle.x + matchedRectangle.width * 0.2,
    y: matchedRectangle.y + matchedRectangle.height * 0.47,
    width: matchedRectangle.width * 0.6,
    height: Math.max(1, matchedRectangle.height * 0.08),
  };

  return {
    id: input.id,
    label: input.label,
    description: input.description,
    outputUrl: URL.createObjectURL(blob),
    matchedUrl: matchedCanvas.toDataURL("image/png"),
    smoothingQuality:
      input.smoothingEnabled === false
        ? "disabled"
        : (input.smoothingQuality ?? "default"),
    alignedToPixels: input.alignedToPixels ?? false,
    sourceKind: input.sourceKind,
    resamplingPasses: 2,
    outputRectangle,
    metrics: {
      ...comparison,
      edgeEnergyRatio:
        edgeEnergy(candidateCrop) / Math.max(0.0001, edgeEnergy(referenceCrop)),
      edgeSpreadPixels: edgeSpreadPixels(matchedPixels, matchedRectangle),
      flatRegionVariance: regionVariance(matchedPixels, flatBounds),
    },
    encodeDecode: {
      meanAbsoluteRgbaDifference: encodeDecode.meanAbsoluteRgbaDifference,
      maximumRgbaDifference: encodeDecode.maximumRgbaDifference,
    },
  };
};

const round = (value: number) => Math.round(value * 10000) / 10000;

export const runImageToolsRenderQualityLab =
  async (): Promise<TRenderLabResult> => {
    const [background, alphaSvg, opaqueSvg] = await Promise.all([
      loadImage(fixtureUrl("background.svg")),
      loadImage(fixtureUrl("sticker-alpha.svg")),
      loadImage(fixtureUrl("sticker-opaque.svg")),
    ]);
    const [alpha96, alpha780, alpha1560, opaque780] = await Promise.all([
      rasterizeImage(alphaSvg, 96, 95),
      rasterizeImage(alphaSvg, 780, 774),
      rasterizeImage(alphaSvg, 1560, 1548),
      rasterizeImage(opaqueSvg, 780, 774),
    ]);
    const profileStripped780 = await decodeBlob(
      await stripPngColorProfile(alpha780.blob),
    );
    const exportRectangle = getStickerOutputPixelRect({
      placement: LAB_PLACEMENT,
      canvasWidth: LAB_CANVAS_SIZE.width,
      canvasHeight: LAB_CANVAS_SIZE.height,
    });
    const imageBitmap =
      typeof window.createImageBitmap === "function"
        ? await window.createImageBitmap(alpha780.image, {
            resizeWidth: Math.max(1, Math.round(exportRectangle.width)),
            resizeHeight: Math.max(1, Math.round(exportRectangle.height)),
            resizeQuality: "high",
          })
        : null;
    const directReference = renderDirectReference({
      background,
      sticker: alpha780.image,
    });
    const directReferencePixels = canvasToImageData(directReference);
    const sourceAspectRatio = 780 / 774;
    const currentPixelWidth = LAB_PLACEMENT.width * LAB_CANVAS_SIZE.width;
    const currentPixelHeight = LAB_PLACEMENT.height * LAB_CANVAS_SIZE.height;
    const preservedPixelWidth = Math.sqrt(
      currentPixelWidth * currentPixelHeight * sourceAspectRatio,
    );
    const preservedPixelHeight = preservedPixelWidth / sourceAspectRatio;
    const aspectPreservedPlacement: TNormalizedStickerPlacement = {
      x:
        LAB_PLACEMENT.x +
        (LAB_PLACEMENT.width - preservedPixelWidth / LAB_CANVAS_SIZE.width) / 2,
      y:
        LAB_PLACEMENT.y +
        (LAB_PLACEMENT.height - preservedPixelHeight / LAB_CANVAS_SIZE.height) /
          2,
      width: preservedPixelWidth / LAB_CANVAS_SIZE.width,
      height: preservedPixelHeight / LAB_CANVAS_SIZE.height,
    };
    const defaultContext = createCanvas(1, 1).getContext("2d");
    if (!defaultContext) throw new Error("Canvas defaults are unavailable.");

    const inputs: TVariantInput[] = [
      {
        id: "current-default",
        label: "Current Canvas behavior",
        description:
          "780 × 774 PNG, browser-default smoothing, fractional output rectangle.",
        sticker: alpha780.image,
        sourceKind: "780px-png",
      },
      {
        id: "smoothing-disabled",
        label: "Smoothing disabled",
        description: "Only imageSmoothingEnabled changes to false.",
        sticker: alpha780.image,
        sourceKind: "780px-png",
        smoothingEnabled: false,
      },
      {
        id: "smoothing-low",
        label: "Explicit low smoothing",
        description: "Only imageSmoothingQuality changes to low.",
        sticker: alpha780.image,
        sourceKind: "780px-png",
        smoothingQuality: "low",
      },
      {
        id: "smoothing-medium",
        label: "Explicit medium smoothing",
        description: "Only imageSmoothingQuality changes to medium.",
        sticker: alpha780.image,
        sourceKind: "780px-png",
        smoothingQuality: "medium",
      },
      {
        id: "smoothing-high",
        label: "Explicit high smoothing",
        description: "Only imageSmoothingQuality changes to high.",
        sticker: alpha780.image,
        sourceKind: "780px-png",
        smoothingQuality: "high",
      },
      {
        id: "integer-aligned",
        label: "Integer-aligned rectangle",
        description:
          "Only the destination edges change from fractional to rounded output pixels.",
        sticker: alpha780.image,
        sourceKind: "780px-png",
        alignedToPixels: true,
      },
      {
        id: "profile-stripped-png",
        label: "Profile-stripped PNG",
        description:
          "Only PNG color-profile and gamma chunks are removed from the source.",
        sticker: profileStripped780.image,
        sourceKind: "780px-profile-stripped",
      },
      {
        id: "high-resolution-png",
        label: "1560 × 1548 PNG source",
        description:
          "Only source resolution changes from built-in-sized to 2×.",
        sticker: alpha1560.image,
        sourceKind: "1560px-png",
      },
      {
        id: "low-resolution-png",
        label: "96 × 95 custom PNG source",
        description:
          "Only source resolution changes to a deliberately undersized custom asset.",
        sticker: alpha96.image,
        sourceKind: "96px-png",
      },
      {
        id: "svg-source",
        label: "SVG source",
        description: "Only the source format changes from 96 px PNG to SVG.",
        sticker: alphaSvg,
        sourceKind: "svg",
      },
      ...(imageBitmap
        ? [
            {
              id: "image-bitmap-high",
              label: "createImageBitmap high resize",
              description:
                "Only the sticker resize pipeline changes to an explicit high-quality destination-sized ImageBitmap.",
              sticker: imageBitmap,
              sourceKind: "image-bitmap" as const,
            },
          ]
        : []),
      {
        id: "aspect-preserved",
        label: "Aspect-ratio preserved",
        description:
          "Only nonuniform stretching changes; area and center are preserved.",
        sticker: alpha780.image,
        sourceKind: "780px-png",
        placement: aspectPreservedPlacement,
      },
      {
        id: "opaque-control",
        label: "Opaque control",
        description:
          "Only transparent/semi-transparent source pixels are removed.",
        sticker: opaque780.image,
        sourceKind: "780px-opaque-png",
      },
    ];

    const variants: TRenderLabVariant[] = [];
    for (const input of inputs) {
      variants.push(
        await createVariant({
          input,
          background,
          directReferencePixels:
            input.id === "opaque-control"
              ? canvasToImageData(
                  renderDirectReference({
                    background,
                    sticker: opaque780.image,
                  }),
                )
              : input.id === "aspect-preserved"
                ? canvasToImageData(
                    renderDirectReference({
                      background,
                      sticker: alpha780.image,
                      placement: aspectPreservedPlacement,
                    }),
                  )
                : directReferencePixels,
        }),
      );
    }

    const directBlob = await canvasToBlob(directReference);
    variants.unshift({
      id: "direct-reference",
      label: "Direct matched-size reference",
      description:
        "Background and original 780 × 774 sticker are each sampled once at final display size.",
      outputUrl: URL.createObjectURL(directBlob),
      matchedUrl: directReference.toDataURL("image/png"),
      smoothingQuality: "direct-dom-equivalent",
      alignedToPixels: false,
      sourceKind: "780px-png",
      resamplingPasses: 1,
      outputRectangle: getStickerOutputPixelRect({
        placement: LAB_PLACEMENT,
        canvasWidth: directReference.width,
        canvasHeight: directReference.height,
      }),
      metrics: {
        meanAbsoluteRgbaDifference: 0,
        maximumRgbaDifference: 0,
        rootMeanSquareRgbaDifference: 0,
        ssim: 1,
        edgeEnergyRatio: 1,
        edgeSpreadPixels: edgeSpreadPixels(
          directReferencePixels,
          getStickerOutputPixelRect({
            placement: LAB_PLACEMENT,
            canvasWidth: directReference.width,
            canvasHeight: directReference.height,
          }),
        ),
        flatRegionVariance: 0,
      },
      encodeDecode: {
        meanAbsoluteRgbaDifference: 0,
        maximumRgbaDifference: 0,
      },
    });

    const byId = (id: string) => {
      const variant = variants.find((item) => item.id === id);
      if (!variant) throw new Error(`Missing render variant: ${id}`);
      return variant;
    };
    const current = byId("current-default");
    const low = byId("smoothing-low");
    const medium = byId("smoothing-medium");
    const high = byId("smoothing-high");
    const aligned = byId("integer-aligned");
    const highResolution = byId("high-resolution-png");
    const svg = byId("svg-source");
    const opaque = byId("opaque-control");
    const imageBitmapVariant = variants.find(
      (variant) => variant.id === "image-bitmap-high",
    );
    const smoothingMeans = [current, low, medium, high].map(
      (variant) => variant.metrics.meanAbsoluteRgbaDifference,
    );

    return {
      generatedAt: new Date().toISOString(),
      userAgent: navigator.userAgent,
      devicePixelRatio: window.devicePixelRatio,
      canvasSize: LAB_CANVAS_SIZE,
      matchedSize: {
        cssWidth: LAB_MATCHED_CSS_SIZE.width,
        cssHeight: LAB_MATCHED_CSS_SIZE.height,
        pixelWidth: directReference.width,
        pixelHeight: directReference.height,
      },
      placement: LAB_PLACEMENT,
      referenceStickerUrl: alpha780.canvas.toDataURL("image/png"),
      defaultContextSettings: {
        imageSmoothingEnabled: defaultContext.imageSmoothingEnabled,
        imageSmoothingQuality: defaultContext.imageSmoothingQuality,
      },
      variants,
      experimentSummary: {
        smoothingMeanDifferenceRange: round(
          Math.max(...smoothingMeans) - Math.min(...smoothingMeans),
        ),
        highVsDefaultMeanDifference: round(
          high.metrics.meanAbsoluteRgbaDifference -
            current.metrics.meanAbsoluteRgbaDifference,
        ),
        alignedVsFractionalMeanDifference: round(
          aligned.metrics.meanAbsoluteRgbaDifference -
            current.metrics.meanAbsoluteRgbaDifference,
        ),
        highResolutionImprovementPercent: round(
          ((current.metrics.meanAbsoluteRgbaDifference -
            highResolution.metrics.meanAbsoluteRgbaDifference) /
            Math.max(0.0001, current.metrics.meanAbsoluteRgbaDifference)) *
            100,
        ),
        svgImprovementPercent: round(
          ((current.metrics.meanAbsoluteRgbaDifference -
            svg.metrics.meanAbsoluteRgbaDifference) /
            Math.max(0.0001, current.metrics.meanAbsoluteRgbaDifference)) *
            100,
        ),
        imageBitmapImprovementPercent: imageBitmapVariant
          ? round(
              ((current.metrics.meanAbsoluteRgbaDifference -
                imageBitmapVariant.metrics.meanAbsoluteRgbaDifference) /
                Math.max(0.0001, current.metrics.meanAbsoluteRgbaDifference)) *
                100,
            )
          : null,
        doubleResamplingPenaltyPercent: round(
          current.metrics.meanAbsoluteRgbaDifference,
        ),
        alphaVsOpaqueErrorDifference: round(
          current.metrics.meanAbsoluteRgbaDifference -
            opaque.metrics.meanAbsoluteRgbaDifference,
        ),
        encodeDecodeMaximumDifference: Math.max(
          ...variants.map(
            (variant) => variant.encodeDecode.maximumRgbaDifference,
          ),
        ),
      },
    };
  };
